import { useApolloClient } from "@apollo/client";
import { useCallback, useState } from "react";

import { HardwareRequestStatus } from "src/api/types.generated";
import { errorToast, successToast } from "src/shared/components";

import {
  GetEventAttendeeForUserDocument,
  GetEventAttendeeForUserQuery,
  GetEventAttendeeForUserQueryVariables,
} from "../../../graphql/getEventAttendeeForUser.generated";
import { useSetEventAttendeeHasIdMutation } from "../../../graphql/setEventAttendeeHasId.generated";

export type THardwareRequestForIdTracking = {
  id: number;
  user_id: number;
  status: HardwareRequestStatus;
};

export type THasIdModalMode = "checkout" | "return";

export type THasIdModalState = {
  mode: THasIdModalMode;
  eventAttendeeId: number;
  userName: string;
  currentHasId: boolean;
};

export const useHardwareIdTracking = (
  hardwareRequests: THardwareRequestForIdTracking[] | undefined
) => {
  const [modalState, setModalState] = useState<THasIdModalState | null>(null);

  const client = useApolloClient();
  const [setEventAttendeeHasId, { loading: isUpdatingHasId }] =
    useSetEventAttendeeHasIdMutation();

  const hasNoOtherOutstandingHardware = useCallback(
    (userId: number, currentRequestId: number) =>
      !(hardwareRequests ?? []).some(
        (request) =>
          request.user_id === userId &&
          request.id !== currentRequestId &&
          request.status === HardwareRequestStatus.PickedUp
      ),
    [hardwareRequests]
  );

  const openPromptIfNeeded = useCallback(
    async (
      mode: THasIdModalMode,
      requestId: number,
      userId: number,
      eventId: number,
      userName: string
    ) => {
      if (!hasNoOtherOutstandingHardware(userId, requestId)) return;

      try {
        // Must not be served from the cache: checkout and return prompt for
        // the same user/event, and the second lookup has to reflect the
        // has_id written by the first prompt's confirmation.
        const { data } = await client.query<
          GetEventAttendeeForUserQuery,
          GetEventAttendeeForUserQueryVariables
        >({
          query: GetEventAttendeeForUserDocument,
          variables: { user_id: userId, event_id: eventId },
          fetchPolicy: "network-only",
        });

        const attendee = data.eventAttendees?.[0];

        if (!attendee) {
          errorToast(
            `Couldn't find a hacker record for ${userName} - unable to track their ID status`
          );
          return;
        }

        // No confirmation needed when returning their last item and we
        // never had an ID on file for them in the first place.
        if (mode === "return" && !attendee.has_id) {
          return;
        }

        setModalState({
          mode,
          eventAttendeeId: attendee.id,
          userName,
          currentHasId: attendee.has_id,
        });
      } catch (e) {
        errorToast("Failed to look up attendee ID status");
      }
    },
    [hasNoOtherOutstandingHardware, client]
  );

  const handleConfirm = useCallback(
    async (hasId: boolean) => {
      if (!modalState) return;

      try {
        await setEventAttendeeHasId({
          variables: {
            event_attendee_id: modalState.eventAttendeeId,
            has_id: hasId,
          },
        });
        successToast(
          hasId
            ? `Marked ${modalState.userName}'s ID as collected`
            : `Marked ${modalState.userName}'s ID as not on file`
        );
      } catch (e) {
        errorToast("Failed to update ID status");
      } finally {
        setModalState(null);
      }
    },
    [modalState, setEventAttendeeHasId]
  );

  return {
    modalState,
    isUpdatingHasId,
    openPromptIfNeeded,
    handleConfirm,
  };
};
